import type { FunctionComponent, ReactNode } from "react";
import { useEffect } from "react";
import type { LoopSourcePreviewTarget } from "../../../../../stores/flow-canvas.store";

interface OptionPreviewRowProps {
  /** What this row previews on the canvas, or undefined if it previews nothing. */
  target: LoopSourcePreviewTarget | undefined;
  onPreview: (target: LoopSourcePreviewTarget | undefined) => void;
  children: ReactNode;
}

/**
 * One row of the Looping "Select List" dropdown, owning the canvas hover
 * preview it drives.
 *
 * Clearing on unmount is the point of this component. A row can vanish with
 * the pointer still on it -- selecting closes the list, and an options rebuild
 * remounts the rows -- and React fires no mouseleave for an element that is
 * removed, so the preview outline stayed on the canvas card with no way left
 * to clear it (STR-14148). Tying teardown to the row's lifetime holds the
 * invariant "the preview never outlives the row that set it" whichever way the
 * list goes away, instead of enumerating dismissal callbacks.
 */
export const OptionPreviewRow: FunctionComponent<OptionPreviewRowProps> = ({
  target,
  onPreview,
  children,
}) => {
  useEffect(() => () => onPreview(undefined), [onPreview]);

  return (
    <div
      onMouseEnter={() => onPreview(target)}
      onMouseLeave={() => onPreview(undefined)}
    >
      {children}
    </div>
  );
};
